import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MessageCircle, Mail, MapPin, Loader2, CheckCircle2, X, Check, Phone } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Chapter } from "./Reveal";
import { fireConfetti } from "../lib/site";
import { useBooking } from "../lib/booking";
import { useContent } from "../lib/content";

const EVENT_TYPES = ["Wedding", "Corporate Event", "Birthday Party", "Private Party", "Beach Celebration", "Other"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-2xl bg-white/[0.07] border border-white/15 text-white placeholder:text-white/35 px-5 py-4 text-sm transition-colors focus:border-[#c9006f]";

export const Contact = () => {
  const { pkg, setPkg, addons, toggleAddon, removeAddon, total } = useBooking();
  const { packages, addons: addonList, settings } = useContent();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_date: "",
    venue: "",
    event_type: "",
    guest_count: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    if (!form.event_date) { setAvailability(null); return; }
    setAvailability("checking");
    const t = setTimeout(() => {
      axios
        .get(`${API}/availability`, { params: { date: form.event_date } })
        .then((r) => setAvailability(r.data.available ? "available" : "booked"))
        .catch(() => setAvailability(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.event_date]);

  useEffect(() => {
    if (!pkg && packages.length > 0) {
      const popular = packages.find((p) => p.popular) || packages[0];
      setPkg(`${popular.name} — $${popular.price}`);
    }
  }, [pkg, packages, setPkg]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_date) {
      toast.error("Please fill in your name, email, and event date.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        package_interest: pkg || "Not sure yet",
        addons: addons.map((a) => a.title),
        estimated_total: `$${total}`,
      });
      setSent(true);
      fireConfetti(e);
      toast.success("Inquiry received! We'll be in touch within 24 hours.");
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const phone = settings?.phone || "+1 (345) 328-6172";
  const bizEmail = settings?.business_email || "info@glamboothcayman.com";
  const waLink = `https://wa.me/${phone.replace(/\D/g, "")}`;
  const telLink = `tel:${phone.replace(/[^\d+]/g, "")}`;

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#3c003a] relative grain overflow-hidden" data-testid="contact-section">
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-16">
        <div>
          <Chapter number="06" label="Book Your Date" dark />
          <Reveal>
            <h2 className="font-serif text-white text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.1] mb-6">
              Let's make it <em className="text-[#c9006f]">unforgettable</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/60 text-base md:text-lg leading-[1.7] mb-12 max-w-md">
              Dates book fast in peak season. Tell us about your event and we'll
              confirm availability within 24 hours.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="space-y-5">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-link"
              className="flex items-center gap-4 text-white/80 hover:text-[#ffc4e4] transition-colors group"
            >
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-white/[0.07] border border-white/15 text-[#c9006f] group-hover:border-[#c9006f]/60 transition-colors">
                <MessageCircle size={20} strokeWidth={1.6} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-[0.2em] text-white/40">WhatsApp</span>
                {phone}
              </span>
            </a>
            <a
              href={`mailto:${bizEmail}`}
              data-testid="email-link"
              className="flex items-center gap-4 text-white/80 hover:text-[#ffc4e4] transition-colors group"
            >
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-white/[0.07] border border-white/15 text-[#c9006f] group-hover:border-[#c9006f]/60 transition-colors">
                <Mail size={20} strokeWidth={1.6} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-[0.2em] text-white/40">Email</span>
                {bizEmail}
              </span>
            </a>
            <div className="flex items-center gap-4 text-white/80">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-white/[0.07] border border-white/15 text-[#c9006f]">
                <MapPin size={20} strokeWidth={1.6} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-[0.2em] text-white/40">Serving</span>
                Grand Cayman & Sister Islands
              </span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          {sent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="h-full min-h-[420px] rounded-3xl bg-white/[0.06] border border-[#c9006f]/40 backdrop-blur-md grid place-items-center p-10 text-center"
              data-testid="inquiry-success"
            >
              <div>
                <CheckCircle2 size={56} className="text-[#c9006f] mx-auto mb-6" strokeWidth={1.2} />
                <h3 className="font-serif text-white text-3xl mb-3">You're on the list!</h3>
                <p className="text-white/60 leading-relaxed max-w-sm mx-auto">
                  Thank you, {form.name.split(" ")[0]}. Our team will confirm your
                  date and package details within 24 hours.
                </p>
              </div>
            </motion.div>
          ) : (
            <form
              onSubmit={submit}
              className="rounded-3xl bg-white/[0.06] border border-white/12 backdrop-blur-md p-8 lg:p-10 space-y-5"
              data-testid="inquiry-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  className={inputCls}
                  placeholder="Your name *"
                  value={form.name}
                  onChange={set("name")}
                  data-testid="inquiry-name-input"
                />
                <input
                  className={inputCls}
                  type="email"
                  placeholder="Email address *"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="inquiry-email-input"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <input
                    className={`${inputCls} [color-scheme:dark]`}
                    type="date"
                    value={form.event_date}
                    onChange={set("event_date")}
                    data-testid="inquiry-date-input"
                    aria-label="Event date"
                  />
                  {availability === "checking" && (
                    <p className="flex items-center gap-1.5 text-white/40 text-xs mt-2 ml-1" data-testid="availability-checking">
                      <Loader2 size={12} className="animate-spin" /> Checking availability...
                    </p>
                  )}
                  {availability === "available" && (
                    <p className="flex items-center gap-1.5 text-emerald-400 text-xs font-semibold mt-2 ml-1" data-testid="availability-available">
                      <CheckCircle2 size={13} /> Great news — this date is available!
                    </p>
                  )}
                  {availability === "booked" && (
                    <p className="flex items-center gap-1.5 text-amber-400 text-xs font-semibold mt-2 ml-1" data-testid="availability-booked">
                      <X size={13} /> This date is already booked — send an inquiry and we'll suggest options.
                    </p>
                  )}
                </div>
                <input
                  className={inputCls}
                  placeholder="Venue / location"
                  value={form.venue}
                  onChange={set("venue")}
                  data-testid="inquiry-venue-input"
                />
              </div>
              <input
                className={inputCls}
                type="tel"
                placeholder="WhatsApp / phone number (optional)"
                value={form.phone}
                onChange={set("phone")}
                data-testid="inquiry-phone-input"
              />
              <div className="grid sm:grid-cols-2 gap-5">
                <select
                  className={`${inputCls} appearance-none [color-scheme:dark]`}
                  value={form.event_type}
                  onChange={set("event_type")}
                  data-testid="inquiry-event-type-select"
                >
                  <option className="text-black" value="">Type of event...</option>
                  {EVENT_TYPES.map((t) => (
                    <option key={t} className="text-black" value={t}>{t}</option>
                  ))}
                </select>
                <input
                  className={inputCls}
                  type="number"
                  min="1"
                  placeholder="Number of guests"
                  value={form.guest_count}
                  onChange={set("guest_count")}
                  data-testid="inquiry-guest-count-input"
                />
              </div>
              <select
                className={`${inputCls} appearance-none [color-scheme:dark]`}
                value={pkg}
                onChange={(e) => setPkg(e.target.value)}
                data-testid="inquiry-package-select"
              >
                {packages.flatMap((p) =>
                  p.tiers?.length
                    ? p.tiers.map((t) => (
                        <option key={p.id + t.label} className="text-black" value={`${p.name} (${t.label}) — $${t.price}`}>
                          {p.name} — ${t.price.toLocaleString()} · {t.label.toLowerCase()}
                        </option>
                      ))
                    : [
                        <option key={p.id} className="text-black" value={`${p.name} — $${p.price}`}>
                          {p.name} — ${p.price} / {p.hours.toLowerCase()}{p.popular ? " (Most Popular)" : ""}
                        </option>,
                      ]
                )}
                <option className="text-black" value="Not sure yet">Not sure yet — help me choose</option>
              </select>
              <div data-testid="inquiry-addons-picker">
                <p className="text-white/45 text-xs uppercase tracking-[0.2em] font-semibold mb-3">
                  Add-Ons
                </p>
                <div className="flex flex-wrap gap-2.5">
                  {addonList.map((a) => {
                    const selected = addons.some((x) => x.title === a.title);
                    return (
                      <button
                        key={a.id || a.title}
                        type="button"
                        onClick={() => toggleAddon(a.title, a.price)}
                        data-testid={`form-addon-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 ${
                          selected
                            ? "bg-[#c9006f] text-white shadow-[0_0_16px_rgba(201,0,111,0.5)]"
                            : "border border-white/20 text-white/70 hover:border-[#c9006f] hover:text-[#ffc4e4]"
                        }`}
                      >
                        {selected && <Check size={12} strokeWidth={3} />}
                        {a.title} · ${a.price}{a.unit}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div
                className="rounded-2xl bg-white/[0.05] border border-white/10 p-5 space-y-3"
                data-testid="booking-summary"
              >
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/70">{pkg || "No package selected"}</span>
                  <span className="text-[#ffc4e4] font-semibold">
                    {pkg && pkg !== "Not sure yet" ? pkg.split("— ")[1] : "—"}
                  </span>
                </div>
                {addons.map((a) => (
                  <div key={a.title} className="flex items-center justify-between text-sm" data-testid={`summary-addon-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <span className="flex items-center gap-2 text-white/70">
                      <button
                        type="button"
                        onClick={() => removeAddon(a.title)}
                        aria-label={`Remove ${a.title}`}
                        data-testid={`remove-addon-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="grid place-items-center w-5 h-5 rounded-full bg-white/10 text-white/60 hover:bg-[#c9006f] hover:text-white transition-colors"
                      >
                        <X size={11} strokeWidth={3} />
                      </button>
                      {a.title}
                    </span>
                    <span className="text-[#ffc4e4] font-semibold">${a.price}</span>
                  </div>
                ))}
                {addons.length === 0 && (
                  <p className="text-white/35 text-xs">
                    No add-ons selected — pick extras above or in the "Level Up Your Event" section.
                  </p>
                )}
                <div className="gold-rule" />
                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold text-sm uppercase tracking-[0.15em]">
                    Estimated Total
                  </span>
                  <span className="font-serif text-[#c9006f] text-2xl" data-testid="booking-total">
                    ${total}
                  </span>
                </div>
              </div>
              <textarea
                className={`${inputCls} min-h-[110px] resize-none`}
                placeholder="Tell us about your event (optional)"
                value={form.message}
                onChange={set("message")}
                data-testid="inquiry-message-input"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="inquiry-submit-btn"
                className="btn-gold w-full rounded-full py-4 text-base font-bold tracking-wide disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? "Sending..." : "Book Your GlamBooth"}
              </button>
              <div className="grid grid-cols-2 gap-4">
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-whatsapp-btn"
                  className="btn-outline-glow rounded-full py-3.5 text-sm font-bold text-white border border-[#25D366]/60 bg-[#25D366]/15 hover:bg-[#25D366]/25 flex items-center justify-center gap-2 transition-colors"
                >
                  <MessageCircle size={17} className="text-[#25D366]" /> WhatsApp Us
                </a>
                <a
                  href={telLink}
                  data-testid="booking-call-btn"
                  className="btn-outline-glow rounded-full py-3.5 text-sm font-bold text-white border border-white/30 hover:text-[#ffc4e4] flex items-center justify-center gap-2 transition-colors"
                >
                  <Phone size={16} className="text-[#ffc4e4]" /> Call Now
                </a>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
